import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "sonner";
import Login from "@/pages/Login";
import AppShell from "@/components/AppShell";

const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Orders = lazy(() => import("@/pages/Orders"));
const OrderDetail = lazy(() => import("@/pages/OrderDetail"));
const NewOrder = lazy(() => import("@/pages/NewOrder"));
const Settings = lazy(() => import("@/pages/Settings"));
const ApiKeys = lazy(() => import("@/pages/ApiKeys"));
const ApiDocs = lazy(() => import("@/pages/ApiDocs"));
const PublicTrack = lazy(() => import("@/pages/PublicTrack"));
const Serviceability = lazy(() => import("@/pages/Serviceability"));
const BulkImport = lazy(() => import("@/pages/BulkImport"));
const Partners = lazy(() => import("@/pages/Partners"));
const Warehouse = lazy(() => import("@/pages/Warehouse"));
const Incoming = lazy(() => import("@/pages/Incoming"));
const Pickup = lazy(() => import("@/pages/Pickup"));
const Branding = lazy(() => import("@/pages/Branding"));
const PartnerTrack = lazy(() => import("@/pages/PartnerTrack"));
const POD = lazy(() => import("@/pages/POD"));

function PageLoader() {
  return (
    <div data-testid="page-loader" className="min-h-screen flex items-center justify-center text-sm text-gray-500">
      Loading…
    </div>
  );
}

function ProtectedLayout() {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-sm text-gray-500">Loading…</div>;
  }
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}

function RequireAdmin() {
  const { user } = useAuth();
  if (user && user.role !== "admin") return <Navigate to="/orders" replace />;
  return <Outlet />;
}

function RequireNotPartner() {
  const { user } = useAuth();
  if (user && user.role === "partner") return <Navigate to="/orders" replace />;
  return <Outlet />;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/track" element={<PublicTrack />} />
          <Route path="/track/:awb" element={<PublicTrack />} />
          <Route path="/t/:slug" element={<PartnerTrack />} />
          <Route path="/pod/:key" element={<POD />} />

          <Route element={<ProtectedLayout />}>
            <Route path="/" element={<Dashboard />} />
            <Route path="/orders" element={<Orders />} />
            <Route path="/orders/new" element={<NewOrder />} />
            <Route path="/orders/:id/edit" element={<NewOrder />} />
            <Route path="/orders/import" element={<BulkImport />} />
            <Route path="/orders/:id" element={<OrderDetail />} />
            <Route path="/serviceability" element={<Serviceability />} />
            <Route path="/warehouse" element={<Warehouse />} />
            <Route element={<RequireNotPartner />}>
              <Route path="/incoming" element={<Incoming />} />
              <Route path="/pickup" element={<Pickup />} />
            </Route>
            <Route path="/branding" element={<Branding />} />
            <Route element={<RequireAdmin />}>
              <Route path="/partners" element={<Partners />} />
              <Route path="/settings" element={<Settings />} />
            </Route>
            <Route path="/keys" element={<ApiKeys />} />
            <Route path="/developer-docs" element={<ApiDocs />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
